import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, TextField, Button } from '@mui/material';
import { toast } from 'react-toastify';
import AuthLayout from '../../components/Auth/AuthLayout';
import { useAuth } from '../../context/AuthContext';
import { checkSupplierProfileComplete } from '../../utils/profileCheck';

const RESEND_COOLDOWN_SECONDS = 45;

/**
 * Entry screen for a 6-digit code sent by email. Serves both flows, told apart
 * by `purpose` in the navigation state:
 *
 *   'register' — proves the supplier owns the address they signed up with.
 *                Grants no session; they go on to sign in.
 *   'login'    — a supplier's second factor, sent by /auth/login once the
 *                password checks out. Signs them in.
 *
 * Suppliers have no authenticator app; this screen is their only second factor.
 */
const EmailCode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { verifyEmail, resendVerification, requestLoginOtp, verifyLoginOtp } = useAuth();

  const email = location.state?.email || '';
  const purpose = location.state?.purpose === 'login' ? 'login' : 'register';
  // Held only to re-request a login code, which the server re-authenticates.
  const password = location.state?.password || '';

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const inputRefs = useRef([]);

  const isLogin = purpose === 'login';

  useEffect(() => {
    if (!email) navigate('/login', { replace: true });
  }, [email, navigate]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const next = [...code];
    next[index] = value;
    setCode(next);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const next = pasted.split('');
    while (next.length < 6) next.push('');
    setCode(next);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const otpCode = code.join('');
    if (otpCode.length < 6) {
      setError('Enter all 6 digits');
      return;
    }

    setLoading(true);
    const result = isLogin
      ? await verifyLoginOtp(email, otpCode)
      : await verifyEmail(email, otpCode);
    setLoading(false);

    if (!result.success) {
      setError(result.message || 'That code is not valid or has expired.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      return;
    }

    if (isLogin) {
      const user = result.user;
      if (user.role === 'supplier') {
        const profileComplete = await checkSupplierProfileComplete(user);
        navigate(profileComplete ? '/dashboard' : '/profile');
      } else {
        // Staff invited with a temporary password have to change it before
        // anything else. The TOTP pages did this and this one did not, so
        // moving staff onto emailed codes would otherwise have let a super
        // admin or management user keep their temporary password — the route
        // guard only covers legal and procurement.
        navigate(user.mustChangePassword ? '/change-password' : '/dashboard');
      }
      return;
    }

    // Registration: the address is proven and that is the whole of enrolment.
    // No session was granted here, so send them to sign in — which mails a
    // fresh code for the login itself.
    toast.success(result.message || 'Email verified. You can now sign in.');
    navigate('/login', { replace: true });
  };

  const handleResend = async () => {
    setError('');
    setCooldown(RESEND_COOLDOWN_SECONDS);
    if (isLogin) {
      await requestLoginOtp(email, password);
    } else {
      await resendVerification(email);
    }
  };

  const otpInputSx = {
    width: { xs: '40px', sm: '48px', md: '56px' },
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
      '& fieldset': { borderColor: '#d1d5db', borderRadius: '8px' },
      '&:hover fieldset': { borderColor: '#9ca3af' },
      '&.Mui-focused fieldset': { borderColor: '#578A18', borderWidth: '1px' },
    },
    '& .MuiOutlinedInput-input': {
      padding: { xs: '12px 0', sm: '14px 0', md: '16px 0' },
      fontSize: { xs: '18px', sm: '20px' },
      fontWeight: 600,
      color: '#000',
      textAlign: 'center',
    },
  };

  return (
    <AuthLayout>
      <Typography
        variant="h4"
        sx={{ fontWeight: 'bold', mb: 1, color: '#000', textAlign: 'center', fontSize: { xs: '22px', sm: '28px' } }}
      >
        {isLogin ? 'Check your email' : 'Verify your email'}
      </Typography>

      <Typography sx={{ color: '#666', mb: isLogin ? 4 : 2, textAlign: 'center', fontSize: '14px', lineHeight: 1.6 }}>
        We sent a 6-digit code to <Box component="span" sx={{ fontWeight: 600, color: '#000' }}>{email}</Box>.
        {isLogin
          ? ' Enter it to finish signing in.'
          : ' Enter it to confirm this address is yours.'}
        <br />
        The code expires in 10 minutes.
      </Typography>

      {/* Registration answers the same way whether or not the address already
          has an account, so this screen is also where someone who already
          registered ends up. Say what they will actually find in their inbox
          rather than leaving them waiting for a code that is not coming. */}
      {!isLogin && (
        <Box
          sx={{
            mb: 4,
            p: 1.5,
            backgroundColor: '#f8f9fa',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            color: '#6b7280',
            fontSize: '13px',
            lineHeight: 1.6,
            textAlign: 'center',
          }}
        >
          Already have an account with this address? We have emailed you about that
          instead —{' '}
          <Box
            component="span"
            onClick={() => navigate('/login')}
            sx={{ color: '#578A18', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
          >
            sign in
          </Box>
          .
        </Box>
      )}

      {error && (
        <Box sx={{ mb: 3, p: 1.5, backgroundColor: '#ffebee', borderRadius: 1, color: '#c62828', fontSize: '14px', textAlign: 'center' }}>
          {error}
        </Box>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, justifyContent: 'center', mb: 3 }}>
          {code.map((digit, i) => (
            <TextField
              key={i}
              inputRef={ref => (inputRefs.current[i] = ref)}
              value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              onPaste={i === 0 ? handlePaste : undefined}
              inputProps={{
                maxLength: 1,
                inputMode: 'numeric',
                autoComplete: i === 0 ? 'one-time-code' : 'off',
                'aria-label': `Digit ${i + 1}`,
                style: { textAlign: 'center' },
              }}
              sx={otpInputSx}
            />
          ))}
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: theme.palette.green.main,
            color: '#fff',
            textTransform: 'none',
            fontSize: '15px',
            fontWeight: 600,
            height: '48px',
            borderRadius: '8px',
            boxShadow: 'none',
            mb: 2,
            '&:hover': { backgroundColor: theme.palette.green.hover, boxShadow: 'none' },
            '&:disabled': { backgroundColor: '#d1d5db' },
          }}
        >
          {loading ? 'Verifying...' : isLogin ? 'Sign in' : 'Verify email'}
        </Button>

        <Typography sx={{ textAlign: 'center', fontSize: '13px', color: '#666', mb: 1 }}>
          {cooldown > 0 ? (
            <>Didn't get it? You can request another in {cooldown}s.</>
          ) : (
            <>
              Didn't get it?{' '}
              <Box
                component="span"
                onClick={handleResend}
                sx={{ color: '#578A18', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
              >
                Send a new code
              </Box>
            </>
          )}
        </Typography>

        <Typography sx={{ textAlign: 'center', fontSize: '13px', color: '#666' }}>
          <Box
            component="span"
            onClick={() => navigate('/login')}
            sx={{ color: '#578A18', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
          >
            Back to Sign In
          </Box>
        </Typography>
      </Box>
    </AuthLayout>
  );
};

export default EmailCode;
